// src/components/PhotoModal.js
// Full-screen photo preview with download options.
import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDownload } from "../hooks/useDownload";
import { useToast } from "./Toast";
import AdSlot from "./AdSlot";

const SIZES = [
  { key: "original", label: "Original",  hint: "Full res" },
  { key: "large",    label: "Large",     hint: "~3200px" },
  { key: "medium",   label: "Medium",    hint: "~1280px" },
  { key: "small",    label: "Small",     hint: "~640px"  },
];

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const ExternalIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
    <polyline points="15 3 21 3 21 9"/>
    <line x1="10" y1="14" x2="21" y2="3"/>
  </svg>
);

const PhotoModal = ({ photo, onClose }) => {
  const { user }                       = useAuth();
  const { downloadPhoto, downloading } = useDownload();
  const { success, error: toastError } = useToast();
  const navigate                       = useNavigate();

  const isDownloading = !!downloading[photo?.id];

  // Close on Escape key
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  if (!photo) return null;

  const handleDownload = async (size) => {
    if (!user) { onClose(); navigate("/auth"); return; }
    try {
      await downloadPhoto(photo, size);
      success(`Downloaded "${photo.photographer}" — ${size}`);
    } catch {
      toastError("Download failed. Please try again.");
    }
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo by ${photo.photographer}`}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="modal__close" onClick={onClose} aria-label="Close preview">
          <CloseIcon />
        </button>

        {/* Image */}
        <div className="modal__image-wrap">
          <img
            src={photo.src.large2x || photo.src.large}
            alt={photo.alt || `Photo by ${photo.photographer}`}
            className="modal__image"
            loading="eager"
          />
        </div>

        {/* Sidebar */}
        <div className="modal__sidebar">
          {/* Photographer */}
          <a
            href={photo.photographer_url}
            target="_blank"
            rel="noopener noreferrer"
            className="modal__photographer"
          >
            <div className="modal__avatar">{photo.photographer[0].toUpperCase()}</div>
            <div>
              <p className="modal__photographer-name">{photo.photographer}</p>
              <p className="modal__photographer-sub">View on Pexels <ExternalIcon /></p>
            </div>
          </a>

          {/* Photo meta */}
          <div className="modal__meta">
            <div className="modal__meta-row">
              <span className="modal__meta-label">Dimensions</span>
              <span className="modal__meta-value">{photo.width} × {photo.height}</span>
            </div>
            {photo.alt && (
              <div className="modal__meta-row">
                <span className="modal__meta-label">Description</span>
                <span className="modal__meta-value modal__meta-value--alt">{photo.alt}</span>
              </div>
            )}
          </div>

          {/* ── AD SLOT: modal sidebar ── */}
          <AdSlot type="pre-download" label="Sponsored" />

          {/* Download options */}
          <div className="modal__downloads">
            <p className="modal__section-label">Download</p>
            {SIZES.map(({ key, label, hint }) => (
              <button
                key={key}
                className="modal__dl-btn"
                onClick={() => handleDownload(key)}
                disabled={isDownloading}
              >
                <span className="modal__dl-info">
                  <span className="modal__dl-label">{label}</span>
                  <span className="modal__dl-hint">{hint}</span>
                </span>
                {isDownloading
                  ? <span className="spinner" />
                  : <DownloadIcon />
                }
              </button>
            ))}
            {!user && (
              <p className="modal__auth-note">
                <a href="/auth" onClick={(e) => { e.preventDefault(); onClose(); navigate("/auth"); }}>
                  Sign in
                </a>{" "}to save your download history.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoModal;
